// EntityDrawer.js - Side drawer for entity details
import React, { useState, useEffect } from 'react';
import { getEntity, getEntityRelations, executeAction, ENTITY_TYPE_CONFIG, SEVERITY_CONFIG } from '../../services/inventoryService';
import './EntityDrawer.css';

function EntityDrawer({ entity, isOpen, onClose }) {
    const [loading, setLoading] = useState(false);
    const [entityDetails, setEntityDetails] = useState(null);
    const [relations, setRelations] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);

    // Load entity details and relations when entity changes
    useEffect(() => {
        if (entity?.entityId) {
            loadEntityData(entity.entityId);
        }
    }, [entity?.entityId]);

    const loadEntityData = async (entityId) => {
        try {
            setLoading(true);
            setError(null);

            const [detailsRes, relationsRes] = await Promise.all([
                getEntity(entityId),
                getEntityRelations(entityId)
            ]);

            setEntityDetails(detailsRes.entity);
            setRelations(relationsRes);
        } catch (err) {
            console.error('Failed to load entity details:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle action execution
    const handleAction = async (action) => {
        try {
            setActionLoading(action);
            setError(null);

            const result = await executeAction(action, entity.entityId);
            alert(`Action queued successfully! Job ID: ${result.jobId}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // Get type config
    const typeConfig = ENTITY_TYPE_CONFIG[entity?.type] || { label: entity?.type, icon: '📦', color: '#888' };
    const severityConfig = entity?.severity ? SEVERITY_CONFIG[entity.severity.toLowerCase()] : null;

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get available actions based on entity type
    const getAvailableActions = () => {
        const actions = [];
        if (entity?.type === 'url' || entity?.type === 'subdomain') {
            actions.push({ id: 'scanNow', label: 'Scan Now', icon: '⚡' });
        }
        if (entity?.type === 'ip') {
            actions.push({ id: 'portScan', label: 'Port Scan', icon: '🔌' });
        }
        if (entity?.type === 'finding') {
            actions.push({ id: 'validateFinding', label: 'Validate', icon: '✓' });
        }
        return actions;
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`drawer-backdrop ${isOpen ? 'open' : ''}`}
                onClick={onClose}
            />

            {/* Drawer */}
            <div className={`entity-drawer ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className="drawer-header">
                    <div className="drawer-title">
                        <span className="entity-type-icon" style={{ color: typeConfig.color }}>
                            {typeConfig.icon}
                        </span>
                        <span className="entity-type-label">{typeConfig.label.replace(/s$/, '')}</span>
                    </div>
                    <button className="drawer-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* Entity Key */}
                <div className="drawer-entity-key">
                    <span className="entity-key-value" title={entity?.key}>
                        {entity?.displayValue || entity?.key}
                    </span>
                    {entity?.displayValue && entity.displayValue !== entity.key && (
                        <span className="entity-full-key">{entity.key}</span>
                    )}
                </div>

                {/* Severity & State Badges */}
                <div className="drawer-badges">
                    {severityConfig && (
                        <span
                            className="badge severity-badge"
                            style={{
                                color: severityConfig.color,
                                backgroundColor: severityConfig.bgColor
                            }}
                        >
                            {severityConfig.label}
                        </span>
                    )}
                    <span className={`badge state-badge state-${entity?.state}`}>
                        {entity?.state}
                    </span>
                    {entity?.isSuppressed && (
                        <span className="badge suppressed-badge">
                            🔇 Suppressed
                        </span>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="drawer-error">
                        <span className="error-icon">⚠</span>
                        {error}
                    </div>
                )}

                {/* Tabs */}
                <div className="drawer-tabs">
                    <button
                        className={`tab ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Details
                    </button>
                    <button
                        className={`tab ${activeTab === 'relations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('relations')}
                    >
                        Relations
                        {relations && (
                            <span className="tab-count">
                                {relations.counts?.parents + relations.counts?.children}
                            </span>
                        )}
                    </button>
                    <button
                        className={`tab ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        History
                    </button>
                </div>

                {/* Tab Content */}
                <div className="drawer-content">
                    {loading ? (
                        <div className="drawer-loading">
                            <div className="loading-spinner"></div>
                            <p>Loading details...</p>
                        </div>
                    ) : (
                        <>
                            {/* Details Tab */}
                            {activeTab === 'details' && (
                                <div className="tab-content">
                                    {/* Asset Info */}
                                    {entity?.assetValue && (
                                        <div className="detail-section">
                                            <h4>Source Asset</h4>
                                            <div className="detail-value asset-link">
                                                {entity.assetValue}
                                            </div>
                                        </div>
                                    )}

                                    {/* Timestamps */}
                                    <div className="detail-section">
                                        <h4>Timeline</h4>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>First Seen</label>
                                                <span>{formatDate(entity?.firstSeenAt)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Last Seen</label>
                                                <span>{formatDate(entity?.lastSeenAt)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Summary Data */}
                                    {entityDetails?.summary && Object.keys(entityDetails.summary).length > 0 && (
                                        <div className="detail-section">
                                            <h4>Properties</h4>
                                            <div className="property-list">
                                                {Object.entries(entityDetails.summary).map(([key, value]) => (
                                                    <div key={key} className="property-item">
                                                        <label>{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                                                        <span className="property-value">
                                                            {Array.isArray(value)
                                                                ? value.join(', ') || '-'
                                                                : typeof value === 'object'
                                                                    ? JSON.stringify(value)
                                                                    : String(value) || '-'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Relations Tab */}
                            {activeTab === 'relations' && (
                                <div className="tab-content">
                                    {/* Parents */}
                                    {relations?.parents?.length > 0 && (
                                        <div className="detail-section">
                                            <h4>Parents ({relations.parents.length})</h4>
                                            <div className="relation-list">
                                                {relations.parents.map((parent, idx) => (
                                                    <div key={idx} className="relation-item">
                                                        <span
                                                            className="relation-type"
                                                            style={{ color: ENTITY_TYPE_CONFIG[parent.type]?.color || '#888' }}
                                                        >
                                                            {ENTITY_TYPE_CONFIG[parent.type]?.icon || '📦'}
                                                        </span>
                                                        <span className="relation-value" title={parent.key}>
                                                            {parent.displayValue}
                                                        </span>
                                                        <span className="relation-edge">{parent.edgeType}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Children */}
                                    {relations?.children?.length > 0 && (
                                        <div className="detail-section">
                                            <h4>Children ({relations.children.length})</h4>
                                            <div className="relation-list">
                                                {relations.children.map((child, idx) => (
                                                    <div key={idx} className="relation-item">
                                                        <span
                                                            className="relation-type"
                                                            style={{ color: ENTITY_TYPE_CONFIG[child.type]?.color || '#888' }}
                                                        >
                                                            {ENTITY_TYPE_CONFIG[child.type]?.icon || '📦'}
                                                        </span>
                                                        <span className="relation-value" title={child.key}>
                                                            {child.displayValue}
                                                        </span>
                                                        <span className="relation-edge">{child.edgeType}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* No Relations */}
                                    {(!relations?.parents?.length && !relations?.children?.length) && (
                                        <div className="empty-relations">
                                            <p>No relationships found for this entity.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* History Tab */}
                            {activeTab === 'history' && (
                                <div className="tab-content">
                                    <div className="detail-section">
                                        <h4>Activity</h4>
                                        <div className="history-list">
                                            <div className="history-item">
                                                <span className="history-date">{formatDate(entity?.lastSeenAt)}</span>
                                                <span className="history-event">Last seen in scan</span>
                                            </div>
                                            {entity?.firstSeenAt !== entity?.lastSeenAt && (
                                                <div className="history-item">
                                                    <span className="history-date">{formatDate(entity?.firstSeenAt)}</span>
                                                    <span className="history-event">First discovered</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Actions Footer */}
                {getAvailableActions().length > 0 && (
                    <div className="drawer-footer">
                        {getAvailableActions().map(action => (
                            <button
                                key={action.id}
                                className="btn-action"
                                onClick={() => handleAction(action.id)}
                                disabled={actionLoading === action.id}
                            >
                                {actionLoading === action.id ? (
                                    <span className="loading-spinner-small"></span>
                                ) : (
                                    <span className="action-icon">{action.icon}</span>
                                )}
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

export default EntityDrawer;
